// src/utils/authFetch.js
// ---------------------------------------------------------------------------
// Attach the session token to calls that go to OUR backend.
//
// WHY AN INTERCEPTOR AND NOT ELEVEN EDITS
// /api/projects is called from eleven components, with API_BASE spelled three
// different ways between them. Threading a header through every call site means
// eleven chances to miss one, and a missed one does not fail loudly — it returns
// 401 and the panel just looks empty. One wrapper, installed once, cannot be
// half-applied.
//
// WHAT IT WILL NOT DO
// It attaches the token only when the request goes to this app's own origin or
// to a Codespaces forwarded port. That matters because the app also calls
// Overpass at overpass-api.de/api/interpreter — a path rule alone would post the
// user's session token to a public API. It also never overwrites an
// Authorization header a caller set deliberately.
// ---------------------------------------------------------------------------

import { readToken } from '../components/AccountPanel';

function isOwnApi(url) {
  try {
    const u = new URL(url, window.location.href);
    if (!u.pathname.startsWith('/api/')) return false;
    return (
      u.host === window.location.host ||
      u.hostname.endsWith('.app.github.dev') ||   // Codespaces forwarded port
      u.hostname === 'localhost' ||
      u.hostname === '127.0.0.1'
    );
  } catch {
    return false;
  }
}

let installed = false;

/** Call once, before the app renders. Safe to call twice. */
export function installAuthFetch() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    const token = readToken();
    if (!token || !url || !isOwnApi(url)) return original(input, init);

    // Headers can arrive as a plain object, an array of pairs or a Headers
    // instance; normalising through Headers handles all three.
    const headers = new Headers(init.headers || (typeof input === 'object' ? input.headers : undefined));
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return original(input, { ...init, headers });
  };
}

export default installAuthFetch;
